import React from 'react';

interface SoWhatCalloutProps {
  children?: React.ReactNode;
  /** Si pasas `bullets`, se renderiza una lista compacta en lugar del
   *  párrafo. Para los casos donde el texto explicativo se siente largo
   *  y queremos algo más escaneable. */
  bullets?: string[];
  /** Si true, se ancla al bottom del contenedor flex (mt-auto). */
  stickToBottom?: boolean;
}

const FALLBACK_BULLET_EMOJIS = ['💡', '✓', '🐾', '✨'];

function emojiForBullet(text: string, idx: number): string {
  const value = text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (/\b(peso|engord|racion|premio|snack|control)\b/.test(value)) return '⚖️';
  if (/\b(articul|cadera|columna|espalda|rodilla|estructura)\b/.test(value)) return '🦴';
  if (/\b(digestion|digestiv|gases|estomago|heces|torsion)\b/.test(value)) return '🌿';
  if (/\b(piel|pelo|muda|omega|pliegues)\b/.test(value)) return '✨';
  if (/\b(energia|actividad|gasto|mental|fisica|activo|musculatura|proteina)\b/.test(value)) return '🔥';
  if (/\b(dientes|boca|oral)\b/.test(value)) return '🦷';
  if (/\b(respir|calor|braquicefal)\b/.test(value)) return '🌬️';
  if (/\b(orejas|oidos)\b/.test(value)) return '👂';
  if (/\b(ojos|vision)\b/.test(value)) return '👁️';
  if (/\b(corazon|cardio)\b/.test(value)) return '❤️';
  if (/\b(urin|hidratacion|agua|rinon|renal)\b/.test(value)) return '💧';
  if (/\b(cachorro|crecimiento|meses)\b/.test(value)) return '📈';
  if (/\b(pequen|tamano|toy)\b/.test(value)) return '📏';

  return FALLBACK_BULLET_EMOJIS[idx % FALLBACK_BULLET_EMOJIS.length];
}

/**
 * Callout azul con 💡 para explicar al usuario por qué se pide un dato
 * (edad, raza, condición corporal, esterilización…) y cómo afecta al plan.
 *
 * Puede recibir `children` (modo párrafo) o `bullets` (modo lista). El
 * modo bullets se prefiere cuando el contenido tiene varios puntos
 * — Santiago lo pidió en la revisión 2026-04-28 para escanear más rápido.
 */
export const SoWhatCallout: React.FC<SoWhatCalloutProps> = ({ children, bullets, stickToBottom }) => (
  <div
    className={`flex items-start gap-2 bg-[#008BBC]/10 border border-[#008BBC]/20 rounded-[14px] p-3${
      stickToBottom ? ' mt-auto' : ''
    }`}
  >
    {(!bullets || bullets.length === 0) && <span className="text-base leading-none mt-0.5">💡</span>}
    <div className="font-poppins text-sm text-[#1a1a1a] leading-snug flex-1">
      {bullets && bullets.length > 0 ? (
        <ul className="space-y-1">
          {bullets.map((b, idx) => (
            <li key={idx} className="flex items-start gap-1.5">
              <span
                className="mt-[1px] inline-flex w-5 flex-shrink-0 justify-center text-[13px] leading-none"
                aria-hidden="true"
              >
                {emojiForBullet(b, idx)}
              </span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      ) : (
        children
      )}
    </div>
  </div>
);
